import { Rate } from "@redotech/fx";
import { Currency } from "@redotech/money/currencies";
import { RateSource } from "@redotech/money/rate-source";
import { LoadState } from "@redotech/react-util/load";
import { BehaviorSubject, EMPTY, Observable, Subscription, timer } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  retry,
  skip,
  switchMap,
} from "rxjs/operators";

/** The (source, pair) a {@link MoneyText} watches. */
export interface WatchRateRef {
  base: Currency;
  quote: Currency;
  source: RateSource;
}

/**
 * Multiplexed streaming transport (typically the `watchFxRates` RPC): given the
 * pairs to watch, emits each one's current rate then deltas over ONE stream,
 * each {@link Rate} self-identifying via `source`/`base`/`quote`. Ends on abort.
 */
export type FxWatchRates = (
  refs: WatchRateRef[],
  signal?: AbortSignal,
) => AsyncIterable<Rate>;

type RateListener = (state: LoadState<Rate>) => void;

interface Pair {
  ref: WatchRateRef;
  refCount: number;
  /** Replays the latest state to every (incl. late) subscriber. */
  state$: BehaviorSubject<LoadState<Rate>>;
}

function keyOf(ref: WatchRateRef): string {
  return `${ref.source}:${ref.base}:${ref.quote}`;
}

function sameKeySet(a: WatchRateRef[], b: WatchRateRef[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const keys = new Set(a.map(keyOf));
  return b.every((ref) => keys.has(keyOf(ref)));
}

/**
 * Consolidates every consumer's rate subscription behind ONE upstream stream.
 * Consumers are ref-counted per pair; the union of watched pairs drives a single
 * {@link FxWatchRates} stream that `switchMap` restarts (aborting the prior one)
 * whenever that union changes. {@link activeRefs} reflects what's mounted.
 */
export interface FxSubscriptionStore {
  /**
   * Register a listener for `ref`'s live rate (latest state replayed
   * immediately). Returns an unsubscribe fn; the pair leaves the stream when its
   * last consumer unsubscribes.
   */
  subscribe(ref: WatchRateRef, listener: RateListener): () => void;
  /** The pairs with at least one mounted consumer right now. */
  activeRefs(): WatchRateRef[];
  /** Observe additions/removals to the active set; returns an unsubscribe fn. */
  onActiveChange(callback: () => void): () => void;
}

/** Build a {@link FxSubscriptionStore} over a multiplexed {@link FxWatchRates} transport. */
export function createFxSubscriptionStore(
  watchRates: FxWatchRates,
): FxSubscriptionStore {
  const pairs = new Map<string, Pair>();
  // The consolidated active set; every change drives the stream + activeRefs.
  const refs$ = new BehaviorSubject<WatchRateRef[]>([]);

  const currentRefs = (): WatchRateRef[] =>
    [...pairs.values()].map((pair) => pair.ref);
  const publishRefs = (): void => refs$.next(currentRefs());

  /** One AsyncIterable stream as an Observable that aborts its transport on teardown. */
  function streamFor(refs: WatchRateRef[]): Observable<Rate> {
    return new Observable<Rate>((subscriber) => {
      const abort = new AbortController();
      void (async () => {
        try {
          for await (const rate of watchRates(refs, abort.signal)) {
            if (abort.signal.aborted) {
              return;
            }
            subscriber.next(rate);
          }
          subscriber.complete();
        } catch (error) {
          if (!abort.signal.aborted) {
            subscriber.error(error);
          }
        }
      })();
      return () => abort.abort();
    });
  }

  // switchMap keeps exactly one stream alive: a new set aborts the previous one
  // and opens a new stream. debounceTime(0) coalesces a synchronous burst of
  // mounts into a single (re)start. retry reconnects after a transient stream
  // failure with capped exponential backoff; only once retries are exhausted
  // does catchError surface the error to listeners and leave the pipeline alive
  // (a later change to the active pair set restarts it).
  const streamSub: Subscription = refs$
    .pipe(
      debounceTime(0),
      distinctUntilChanged(sameKeySet),
      switchMap((refs) =>
        refs.length === 0
          ? EMPTY
          : streamFor(refs).pipe(
              retry({
                count: 5,
                delay: (_err, retryCount) =>
                  timer(Math.min(1000 * 2 ** (retryCount - 1), 30_000)),
              }),
              catchError((error) => {
                for (const pair of pairs.values()) {
                  pair.state$.next({ pending: false, error });
                }
                return EMPTY;
              }),
            ),
      ),
    )
    .subscribe((rate) => {
      pairs.get(keyOf(rate))?.state$.next({ pending: false, value: rate });
    });
  void streamSub;

  return {
    subscribe(ref, listener) {
      const key = keyOf(ref);
      let pair = pairs.get(key);
      const isNew = !pair;
      if (!pair) {
        pair = {
          ref,
          refCount: 0,
          state$: new BehaviorSubject<LoadState<Rate>>({ pending: true }),
        };
        pairs.set(key, pair);
      }
      pair.refCount++;
      const sub = pair.state$.subscribe(listener);
      if (isNew) {
        publishRefs(); // new pair → restart the stream to include it
      }

      let done = false;
      return () => {
        if (done) {
          return;
        }
        done = true;
        sub.unsubscribe();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        pair!.refCount--;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (pair!.refCount === 0) {
          pairs.delete(key);
          publishRefs(); // pair gone → restart without it (or stop if last)
        }
      };
    },
    activeRefs() {
      return currentRefs();
    },
    onActiveChange(callback) {
      // skip(1): ignore the BehaviorSubject's initial replay — fire on changes only.
      const sub = refs$
        .pipe(skip(1), distinctUntilChanged(sameKeySet))
        .subscribe(() => callback());
      return () => sub.unsubscribe();
    },
  };
}
